/**
 * ONE ERROR TYPE FOR THE WHOLE FEATURE, so a screen branches on a status and a code rather than on
 * a message.
 *
 * `Problem.title` and `Problem.detail` are explicitly NOT the contract — the generated types say so
 * on the schema itself: "The machine code a client branches on. This is the contract; `title` and
 * `detail` are not." So nothing in this feature renders `detail` to an author, and the UI copy is
 * ours (see ../strings.ts). `detail` is kept on the object because it is worth having in a bug
 * report, not because a screen prints it.
 */
import type { ErrorCode, Problem } from './contract';

/** Where the failure happened. `http` is a real answer from the API; the others never reached it. */
export type ApiFailureKind =
  /** The request never completed: DNS, TLS, offline, CORS, an aborted navigation. */
  | 'network'
  /** The API answered with a non-2xx status. `status` and `problem` are meaningful. */
  | 'http'
  /** Something answered, but not with the shape the contract promises. A proxy error page does this. */
  | 'shape';

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  /** The HTTP status, or 0 when the request never got one. */
  readonly status: number;
  /** The parsed problem body when there was one. Never rendered verbatim to an author. */
  readonly problem: Problem | null;

  constructor(kind: ApiFailureKind, status: number, problem: Problem | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.problem = problem;
  }

  /** The one thing a screen is allowed to branch on besides the status. */
  get errorCode(): ErrorCode | null {
    return this.problem?.error_code ?? null;
  }
}

/**
 * True when the session is gone. Every screen hands this to the shell's re-auth path and NEVER
 * renders "support is broken" for it — a 401 is not a fault of the page the reader is on.
 */
export function isUnauthenticated(error: ApiError): boolean {
  return error.status === 401;
}

/**
 * True when the API answered "verified, but not permitted".
 *
 * `getPlatformContacts` declares a bare `Problem403` that names NO error code
 * (`authors-api.yaml`), so which of `AUTHOR_CAPABILITY_REQUIRED` / `AUTHOR_DISABLED`
 * applies is not fixed by the corpus. That is why this predicate tests the status and the screens
 * never switch on the code here.
 */
export function isForbidden(error: ApiError): boolean {
  return error.status === 403;
}

/**
 * True when the failure is ours and retrying is reasonable: a 503, a 5xx, or a request that never
 * arrived.
 *
 * `getCurrentTerms` declares a 503 and `getPlatformContacts` does not — nothing about the contacts
 * route makes it immune, it reads the same database through the same process, so this feature
 * handles one for both. That asymmetry is reported as a gap rather than papered over.
 */
export function isRetryable(error: ApiError): boolean {
  return error.kind === 'network' || error.status >= 500 || error.status === 429;
}

/** Turn anything thrown into an `ApiError`, so no screen ever sees a bare `unknown`. */
export function toApiError(thrown: unknown): ApiError {
  if (thrown instanceof ApiError) {
    return thrown;
  }
  const message = thrown instanceof Error ? thrown.message : String(thrown);
  return new ApiError('network', 0, null, message);
}

/**
 * A 429, the one refusal that can say how long to wait: `retryAfterSeconds` is the answer's
 * `Retry-After` when it carried one the app's reader could read (`retryAfterOf`,
 * ../../cabinet/api/problem.ts, through ./transport.ts). A kind of `ApiError` rather than a new
 * member of it, so every branch on `status === 429` above holds unchanged and only the screen that
 * says the wait has to know it exists.
 */
export class ThrottledError extends ApiError {
  readonly retryAfterSeconds: number | undefined;

  constructor(problem: Problem | null, message: string, retryAfterSeconds: number | undefined) {
    super('http', 429, problem, message);
    this.retryAfterSeconds = retryAfterSeconds;
  }
}
